import React, { useState } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  Snackbar,
  Select,
  MenuItem,
  Chip,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CloudDownload as DownloadIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

import toast from 'react-hot-toast';
import { settlementService, type GenerateReportRequest, type ScheduledReport, type ScheduledReportRequest } from '@/services/settlementService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`export-tabpanel-${index}`}
      aria-labelledby={`export-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface ExportDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface DownloadedReport {
  id: string;
  report_name: string;
  generated_at: string;
  file_url?: string;
}

// New scheduled report form state
interface NewScheduledReportForm {
  id: string;
  report_name: string;
  scheduled_frequency: 'daily' | 'weekly' | 'monthly';
  scheduled_time: string;
  scheduled_day: number;
  scheduled_date: number;
  report_format: 'xlsx' | 'csv' | 'zip' | 'pdf';
  selectedColumns: string[];
  isExpanded: boolean;
  isSaving: boolean;
}

// Available columns for export
const AVAILABLE_COLUMNS =  [
    {"id": "settlement_id", "label": "Settlement ID", "format": "text"},
    {"id": "merchant_name", "label": "Merchant", "format": "text"},
    {"id": "settlement_status", "label": "Status", "format": "text"},
    {"id": "settlement_type", "label": "Type", "format": "text"},
    {"id": "total_transaction_amount", "label": "Total Transaction Amount", "format": "currency"},
    {"id": "settlement_amount", "label": "Settlement Amount", "format": "currency"},
    {"id": "settlement_charges", "label": "Settlement Charges", "format": "currency"},
    {"id": "service_tax", "label": "Service Tax", "format": "currency"},
    {"id": "settlement_initiated_on", "label": "Initiated On", "format": "datetime"},
    {"id": "settlement_date", "label": "Settlement Date", "format": "datetime"},
    {"id": "is_settled", "label": "Is Settled", "format": "text"},
    {"id": "utr_number", "label": "UTR Number", "format": "text"},
    {"id": "payout_date_time", "label": "Payout Date/Time", "format": "datetime"},
    {"id": "payout_amount", "label": "Payout Amount", "format": "currency"},
    {"id": "remarks", "label": "Remarks", "format": "text"},
] as const;

// Available file formats
const FILE_FORMATS = [
  { id: 'xlsx', label: 'XLSX' },
  { id: 'csv', label: 'CSV' },
  { id: 'zip', label: 'ZIP' },
  { id: 'pdf', label: 'PDF' },
] as const;

type FileFormat = 'xlsx' | 'csv' | 'zip' | 'pdf';
type DateRangeFilter = 'today' | 'last_3_days' | 'last_7_days' | 'last_30_days' | 'last_60_days' | 'this_month' | 'custom';

export const ExportDrawer: React.FC<ExportDrawerProps> = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [reportName, setReportName] = useState('');
  const [selectedFormat, setSelectedFormat] = useState<FileFormat>('xlsx');
  const [dateRange, setDateRange] = useState<DateRangeFilter>('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    AVAILABLE_COLUMNS.map((col) => col.id)
  );
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Scheduled reports state
  const [scheduledReports, setScheduledReports] = useState<ScheduledReport[]>([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [newReportForms, setNewReportForms] = useState<NewScheduledReportForm[]>([]);
  const [expandedReportId, setExpandedReportId] = useState<string | null>(null);
  const [deletingReportId, setDeletingReportId] = useState<string | null>(null);

  // Downloaded reports state
  const [downloadedReports, setDownloadedReports] = useState<DownloadedReport[]>([]);
  const [isLoadingDownloads, setIsLoadingDownloads] = useState(false);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleColumnToggle = (columnId: string) => {
    setSelectedColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  };

  const handleSelectAllColumns = () => {
    if (selectedColumns.length === AVAILABLE_COLUMNS.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(AVAILABLE_COLUMNS.map((col) => col.id));
    }
  };

  const handleExport = async () => {
    if (!reportName.trim()) {
      toast.error('Please enter a report name');
      return;
    }

    if (selectedColumns.length === 0) {
      toast.error('Please select at least one column');
      return;
    }

    if (dateRange === 'custom' && (!customStartDate || !customEndDate)) {
      toast.error('Please select both start and end dates for custom range');
      return;
    }

    setIsExporting(true);

    try {
      // Calculate date range based on selection
      let startDate = '';
      let endDate = '';

      if (dateRange === 'custom') {
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        const today = new Date();
        endDate = today.toISOString().split('T')[0];

        switch (dateRange) {
          case 'today':
            startDate = endDate;
            break;
          case 'last_3_days':
            startDate = new Date(today.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            break;
          case 'last_7_days':
            startDate = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            break;
          case 'last_30_days':
            startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            break;
          case 'last_60_days':
            startDate = new Date(today.getTime() - 59 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            break;
          case 'this_month':
            startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
            break;
          default:
            startDate = endDate;
        }
      }

      const request: GenerateReportRequest = {
        report_name: reportName.trim(),
        request_report_type: 'quick',
        report_type: 'settlements',
        report_format: selectedFormat,
        report_start_date: startDate,
        report_end_date: endDate,
        column_config: selectedColumns,
      };

      const blob = await settlementService.generateReport(request);

      // Create download link and trigger download automatically
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportName.trim()}.${selectedFormat}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Show success message
      toast.success('Report generated successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export report';
      toast.error(errorMessage);
      console.error('Export error:', err);
    } finally {
      setIsExporting(false);
    }
  };

  // Load scheduled reports when tab changes
  const loadScheduledReports = async () => {
    setIsLoadingReports(true);
    try {
      const response = await settlementService.getScheduledReportsPaginated('settlements', 1, 50);
      // Map the API response to the component's expected format
      const mappedReports: ScheduledReport[] = (response.results || []).map((item) => ({
        id: item.schedule_id,
        report_name: item.report_name,
        scheduled_frequency: item.frequency,
        scheduled_time: item.time,
        scheduled_day: item.day ?? undefined,
        scheduled_date: item.date ?? undefined,
        report_format: item.format,
        column_config: [],
        created_at: item.created_at,
        is_active: true,
      }));
      setScheduledReports(mappedReports);
    } catch (err) {
      console.error('Failed to load scheduled reports:', err);
    } finally {
      setIsLoadingReports(false);
    }
  };

  // Add new report form
  const handleAddNewReportForm = () => {
    const newForm: NewScheduledReportForm = {
      id: `new-${Date.now()}`,
      report_name: '',
      scheduled_frequency: 'daily',
      scheduled_time: '12:00',
      scheduled_day: 1, // Monday
      scheduled_date: 1, // 1st of month
      report_format: 'xlsx',
      selectedColumns: AVAILABLE_COLUMNS.map((col) => col.id),
      isExpanded: true,
      isSaving: false,
    };
    setNewReportForms((prev) => [...prev, newForm]);
  };

  // Update new report form field
  const updateNewReportForm = (formId: string, field: keyof NewScheduledReportForm, value: any) => {
    setNewReportForms((prev) =>
      prev.map((form) =>
        form.id === formId ? { ...form, [field]: value } : form
      )
    );
  };

  // Toggle column selection for new report form
  const toggleNewReportColumn = (formId: string, columnId: string) => {
    setNewReportForms((prev) =>
      prev.map((form) => {
        if (form.id !== formId) return form;
        const newColumns = form.selectedColumns.includes(columnId)
          ? form.selectedColumns.filter((id) => id !== columnId)
          : [...form.selectedColumns, columnId];
        return { ...form, selectedColumns: newColumns };
      })
    );
  };

  // Save new scheduled report
  const handleSaveScheduledReport = async (formId: string) => {
    const form = newReportForms.find((f) => f.id === formId);
    if (!form) return;

    if (!form.report_name.trim()) {
      setError('Please enter a report name');
      return;
    }

    if (form.selectedColumns.length === 0) {
      setError('Please select at least one column');
      return;
    }

    updateNewReportForm(formId, 'isSaving', true);

    try {
      const today = new Date();
      const request: ScheduledReportRequest = {
        report_name: form.report_name.trim(),
        request_report_type: 'scheduled',
        report_type: 'settlements',
        report_format: form.report_format,
        scheduled_frequency: form.scheduled_frequency,
        scheduled_time: `${form.scheduled_time}:00`,
        report_start_date: today.toISOString().split('T')[0],
        report_end_date: today.toISOString().split('T')[0],
        column_config: form.selectedColumns,
      };

      // Add day/date based on frequency
      if (form.scheduled_frequency === 'weekly') {
        request.scheduled_day = form.scheduled_day;
      } else if (form.scheduled_frequency === 'monthly') {
        request.scheduled_date = form.scheduled_date;
      }

      await settlementService.createScheduledReport(request);

      // Remove the form and reload reports
      setNewReportForms((prev) => prev.filter((f) => f.id !== formId));
      toast.success('Scheduled report created successfully');
      await loadScheduledReports();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create scheduled report';
      setError(errorMessage);
    } finally {
      updateNewReportForm(formId, 'isSaving', false);
    }
  };

  // Remove new report form (cancel)
  const handleRemoveNewReportForm = (formId: string) => {
    setNewReportForms((prev) => prev.filter((f) => f.id !== formId));
  };

  // Delete existing scheduled report
  const handleDeleteScheduledReport = async (reportId: string) => {
    setDeletingReportId(reportId);
    try {
      await settlementService.deleteScheduledReport(reportId, 'settlements');
      setScheduledReports((prev) => prev.filter((r) => r.id !== reportId));
      toast.success('Scheduled report deleted successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete scheduled report';
      setError(errorMessage);
    } finally {
      setDeletingReportId(null);
    }
  };

  // Toggle expand/collapse for existing report
  const toggleReportExpanded = (reportId: string) => {
    setExpandedReportId((prev) => (prev === reportId ? null : reportId));
  };

  // Helper to format time for display
  const formatTimeDisplay = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Helper to get frequency display text
  const getFrequencyDisplay = (report: ScheduledReport) => {
    switch (report.scheduled_frequency) {
      case 'daily':
        return `Daily at ${formatTimeDisplay(report.scheduled_time)}`;
      case 'weekly':
        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        return `Every ${days[report.scheduled_day || 0]} at ${formatTimeDisplay(report.scheduled_time)}`;
      case 'monthly':
        return `Monthly on day ${report.scheduled_date || 1} at ${formatTimeDisplay(report.scheduled_time)}`;
      default:
        return '';
    }
  };

  // Load download reports from API
  const loadDownloadReports = async () => {
    setIsLoadingDownloads(true);
    try {
      const response = await settlementService.getDownloadReports('settlements');
      // Transform API response to match component's expected format
      // const reports: DownloadedReport[] = (response?.reports || []).map((report: any) => ({
      //   id: report.id || report.report_id || String(Math.random()),
      //   fileName: report.report_name || report.report_name || 'Unnamed Report',
      //   creationDate: report.generated_at || report.generated_at || new Date().toLocaleString(),
      //   isActive: report.is_active !== undefined ? report.is_active : true,
      //   downloadUrl: report.file_url || report.file_url,
      // }));
      setDownloadedReports(response?.reports || []);
    } catch (err) {
      console.error('Failed to load download reports:', err);
      setDownloadedReports([]);
    } finally {
      setIsLoadingDownloads(false);
    }
  };

  // Load reports when scheduled tab or downloads tab is selected
  React.useEffect(() => {
    if (activeTab === 1 && open) {
      loadScheduledReports();
    }
    if (activeTab === 2 && open) {
      loadDownloadReports();
    }
  }, [activeTab, open]);

  const getDateRangeLabel = (range: DateRangeFilter) => {
    const today = new Date();
    const formatDate = (date: Date) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}`;
    };

    const formatDateFull = (date: Date) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    };

    switch (range) {
      case 'today': {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return `${days[today.getDay()]}, ${formatDate(today)}`;
      }
      case 'last_3_days': {
        const threeDaysAgo = new Date(today);
        threeDaysAgo.setDate(today.getDate() - 2);
        return `${formatDate(threeDaysAgo)} - ${formatDate(today)}`;
      }
      case 'last_7_days': {
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 6);
        return `${formatDate(sevenDaysAgo)} - ${formatDate(today)}`;
      }
      case 'last_30_days': {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 29);
        return `${formatDate(thirtyDaysAgo)} - ${formatDate(today)}`;
      }
      case 'last_60_days': {
        const sixtyDaysAgo = new Date(today);
        sixtyDaysAgo.setDate(today.getDate() - 59);
        return `${formatDate(sixtyDaysAgo)} - ${formatDate(today)}`;
      }
      case 'this_month': {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[today.getMonth()]} ${today.getFullYear()}`;
      }
      case 'custom': {
        if (customStartDate && customEndDate) {
          return `${formatDateFull(new Date(customStartDate))} - ${formatDateFull(new Date(customEndDate))}`;
        }
        return 'Select dates';
      }
      default:
        return '';
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 600 },
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'rgb(23, 166, 218)' }}>
            Export
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="export tabs"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 48,
              },
              '& .Mui-selected': {
                color: 'rgb(23, 166, 218)',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'rgb(23, 166, 218)',
              },
            }}
          >
            <Tab label="Quick export" />
            <Tab label="Scheduled reports" />
            <Tab label="Downloads" />
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
          {/* Quick Export Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Info Banner */}
              <Alert
                icon={<InfoIcon sx={{ color: 'rgb(23, 166, 218)' }} />}
                severity="info"
                sx={{
                  backgroundColor: '#e3f2fd',
                  '& .MuiAlert-icon': {
                    color: 'rgb(23, 166, 218)',
                  },
                }}
              >
                This file will be downloaded with the applied filters (if any). Go to the filters section to modify them.
              </Alert>

              {/* Report Name */}
              <FormControl fullWidth>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Report name
                </Typography>
                <TextField
                  placeholder="Enter a name"
                  value={reportName}
                  onChange={(e) => setReportName(e.target.value)}
                  size="small"
                  fullWidth
                />
              </FormControl>

              {/* File Format */}
              <FormControl>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  File Format
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {FILE_FORMATS.map((format) => (
                    <FormControlLabel
                      key={format.id}
                      control={
                        <Checkbox
                          checked={selectedFormat === format.id}
                          onChange={() => setSelectedFormat(format.id as FileFormat)}
                          sx={{
                            color: 'rgb(23, 166, 218)',
                            '&.Mui-checked': { color: 'rgb(23, 166, 218)' },
                          }}
                        />
                      }
                      label={format.label}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.5,
                        m: 0,
                        backgroundColor: selectedFormat === format.id ? 'rgba(62, 79, 224, 0.08)' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(62, 79, 224, 0.04)',
                        },
                      }}
                    />
                  ))}
                </Box>
              </FormControl>

              {/* Date Range */}
              <FormControl>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                  Date range
                </Typography>
                <RadioGroup
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value as DateRangeFilter)}
                >
                  <FormControlLabel
                    value="today"
                    control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', minWidth: 400 }}>
                        <span>Today</span>
                        <span style={{ color: '#666' }}>{getDateRangeLabel('today')}</span>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="last_3_days"
                    control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', minWidth: 400 }}>
                        <span>Last 3 days</span>
                        <span style={{ color: '#666' }}>{getDateRangeLabel('last_3_days')}</span>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="last_7_days"
                    control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', minWidth: 400 }}>
                        <span>Last 7 days</span>
                        <span style={{ color: '#666' }}>{getDateRangeLabel('last_7_days')}</span>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="last_30_days"
                    control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', minWidth: 400 }}>
                        <span>Last 30 days</span>
                        <span style={{ color: '#666' }}>{getDateRangeLabel('last_30_days')}</span>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="last_60_days"
                    control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', minWidth: 400 }}>
                        <span>Last 60 days</span>
                        <span style={{ color: '#666' }}>{getDateRangeLabel('last_60_days')}</span>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="this_month"
                    control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', minWidth: 400 }}>
                        <span>This month</span>
                        <span style={{ color: '#666' }}>{getDateRangeLabel('this_month')}</span>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="custom"
                    control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                    label={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', minWidth: 400 }}>
                        <span>Custom</span>
                        <span style={{ color: '#666' }}>{getDateRangeLabel('custom')}</span>
                      </Box>
                    }
                  />
                </RadioGroup>

                {/* Custom Date Range Inputs */}
                {dateRange === 'custom' && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 2, ml: 4 }}>
                    <TextField
                      label="Start Date"
                      type="date"
                      value={customStartDate}
                      onChange={(e) => setCustomStartDate(e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="End Date"
                      type="date"
                      value={customEndDate}
                      onChange={(e) => setCustomEndDate(e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                )}
              </FormControl>

              {/* Columns Selection */}
              <FormControl>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Columns
                  </Typography>
                  <Button
                    size="small"
                    onClick={handleSelectAllColumns}
                    sx={{
                      textTransform: 'none',
                      color: 'rgb(23, 166, 218)',
                    }}
                  >
                    {selectedColumns.length === AVAILABLE_COLUMNS.length ? 'Deselect All' : 'Select All'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {AVAILABLE_COLUMNS.map((column) => (
                    <FormControlLabel
                      key={column.id}
                      control={
                        <Checkbox
                          checked={selectedColumns.includes(column.id)}
                          onChange={() => handleColumnToggle(column.id)}
                          size="small"
                          sx={{
                            color: 'rgb(23, 166, 218)',
                            '&.Mui-checked': { color: 'rgb(23, 166, 218)' },
                          }}
                        />
                      }
                      label={column.label}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 1,
                        px: 1,
                        py: 0.25,
                        m: 0,
                        backgroundColor: selectedColumns.includes(column.id)
                          ? 'rgba(62, 79, 224, 0.08)'
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: 'rgba(62, 79, 224, 0.04)',
                        },
                      }}
                    />
                  ))}
                </Box>
              </FormControl>
            </Box>
          </TabPanel>

          {/* Scheduled Reports Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Info Banner */}
              <Alert
                icon={<InfoIcon sx={{ color: 'rgb(23, 166, 218)' }} />}
                severity="info"
                sx={{
                  backgroundColor: '#e3f2fd',
                  '& .MuiAlert-icon': {
                    color: 'rgb(23, 166, 218)',
                  },
                }}
              >
                Schedule reports to be sent automatically via email.
              </Alert>

              {/* Loading State */}
              {isLoadingReports && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: 'rgb(23, 166, 218)' }} />
                </Box>
              )}

              {/* Existing Scheduled Reports List */}
              {!isLoadingReports && scheduledReports.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {scheduledReports.map((report) => (
                    <Paper
                      key={report.id}
                      elevation={1}
                      sx={{
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {report.report_name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666' }}>
                            {getFrequencyDisplay(report)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Delete report">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteScheduledReport(report.id)}
                              disabled={deletingReportId === report.id}
                              sx={{ color: '#e53935' }}
                            >
                              {deletingReportId === report.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <DeleteIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => toggleReportExpanded(report.id)}
                          >
                            {expandedReportId === report.id ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Box>
                      </Box>
                      <Collapse in={expandedReportId === report.id}>
                        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Chip
                              label={report.report_format.toUpperCase()}
                              size="small"
                              sx={{ backgroundColor: 'rgba(62, 79, 224, 0.08)' }}
                            />
                            {report.column_config?.map((col) => {
                              const column = AVAILABLE_COLUMNS.find((c) => c.id === col);
                              return (
                                <Chip
                                  key={col}
                                  label={column?.label || col}
                                  size="small"
                                  variant="outlined"
                                />
                              );
                            })}
                          </Box>
                        </Box>
                      </Collapse>
                    </Paper>
                  ))}
                </Box>
              )}

              {/* New Report Forms */}
              {newReportForms.map((form) => (
                <Paper
                  key={form.id}
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#fafafa',
                      borderBottom: '1px solid #e0e0e0',
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      New report
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveNewReportForm(form.id)}
                          sx={{ color: '#e53935' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => updateNewReportForm(form.id, 'isExpanded', !form.isExpanded)}
                      >
                        {form.isExpanded ? (
                          <KeyboardArrowUpIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowDownIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                  </Box>

                  <Collapse in={form.isExpanded}>
                    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      {/* Report Name */}
                      <FormControl fullWidth>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Report name
                        </Typography>
                        <TextField
                          placeholder="Enter a name"
                          value={form.report_name}
                          onChange={(e) => updateNewReportForm(form.id, 'report_name', e.target.value)}
                          size="small"
                          fullWidth
                        />
                      </FormControl>

                      {/* Time Frame */}
                      <FormControl>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          Time frame
                          <Tooltip title="Choose how often the report should be generated">
                            <InfoIcon sx={{ fontSize: 16, color: '#999' }} />
                          </Tooltip>
                        </Typography>
                        <RadioGroup
                          value={form.scheduled_frequency}
                          onChange={(e) => updateNewReportForm(form.id, 'scheduled_frequency', e.target.value)}
                        >
                          {/* Daily Option */}
                          <Box sx={{ mb: 1 }}>
                            <FormControlLabel
                              value="daily"
                              control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Daily</Typography>
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    Last 24-hour report to be delivered at specific time.
                                  </Typography>
                                </Box>
                              }
                            />
                            {form.scheduled_frequency === 'daily' && (
                              <Box sx={{ ml: 4, mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">Every day at</Typography>
                                <TextField
                                  type="time"
                                  value={form.scheduled_time}
                                  onChange={(e) => updateNewReportForm(form.id, 'scheduled_time', e.target.value)}
                                  size="small"
                                  sx={{ width: 130 }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Box>
                            )}
                          </Box>

                          {/* Weekly Option */}
                          <Box sx={{ mb: 1 }}>
                            <FormControlLabel
                              value="weekly"
                              control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Weekly</Typography>
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    Last 7-day report to be delivered on specific day.
                                  </Typography>
                                </Box>
                              }
                            />
                            {form.scheduled_frequency === 'weekly' && (
                              <Box sx={{ ml: 4, mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2">Every</Typography>
                                <Select
                                  value={form.scheduled_day}
                                  onChange={(e) => updateNewReportForm(form.id, 'scheduled_day', e.target.value)}
                                  size="small"
                                  sx={{ minWidth: 120 }}
                                >
                                  <MenuItem value={0}>Monday</MenuItem>
                                  <MenuItem value={1}>Tuesday</MenuItem>
                                  <MenuItem value={2}>Wednesday</MenuItem>
                                  <MenuItem value={3}>Thursday</MenuItem>
                                  <MenuItem value={4}>Friday</MenuItem>
                                  <MenuItem value={5}>Saturday</MenuItem>
                                  <MenuItem value={6}>Sunday</MenuItem>
                                </Select>
                                <Typography variant="body2">at</Typography>
                                <TextField
                                  type="time"
                                  value={form.scheduled_time}
                                  onChange={(e) => updateNewReportForm(form.id, 'scheduled_time', e.target.value)}
                                  size="small"
                                  sx={{ width: 130 }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Box>
                            )}
                          </Box>

                          {/* Monthly Option */}
                          <Box sx={{ mb: 1 }}>
                            <FormControlLabel
                              value="monthly"
                              control={<Radio sx={{ color: 'rgb(23, 166, 218)', '&.Mui-checked': { color: 'rgb(23, 166, 218)' } }} />}
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Monthly</Typography>
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    Last closed month report to be delivered on specific day.
                                  </Typography>
                                </Box>
                              }
                            />
                            {form.scheduled_frequency === 'monthly' && (
                              <Box sx={{ ml: 4, mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2">Every month on day</Typography>
                                <Select
                                  value={form.scheduled_date}
                                  onChange={(e) => updateNewReportForm(form.id, 'scheduled_date', e.target.value)}
                                  size="small"
                                  sx={{ minWidth: 70 }}
                                >
                                  {Array.from({ length: 31 }, (_, i) => (
                                    <MenuItem key={i + 1} value={i + 1}>
                                      {i + 1}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography variant="body2">at</Typography>
                                <TextField
                                  type="time"
                                  value={form.scheduled_time}
                                  onChange={(e) => updateNewReportForm(form.id, 'scheduled_time', e.target.value)}
                                  size="small"
                                  sx={{ width: 130 }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Box>
                            )}
                          </Box>
                        </RadioGroup>
                      </FormControl>

                      {/* Format Selection */}
                      <FormControl>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Format
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {FILE_FORMATS.map((format) => (
                            <FormControlLabel
                              key={format.id}
                              control={
                                <Checkbox
                                  checked={form.report_format === format.id}
                                  onChange={() => updateNewReportForm(form.id, 'report_format', format.id)}
                                  sx={{
                                    color: 'rgb(23, 166, 218)',
                                    '&.Mui-checked': { color: 'rgb(23, 166, 218)' },
                                  }}
                                />
                              }
                              label={format.label}
                              sx={{
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                px: 1.5,
                                py: 0.5,
                                m: 0,
                                backgroundColor: form.report_format === format.id ? 'rgba(62, 79, 224, 0.08)' : 'transparent',
                                '&:hover': {
                                  backgroundColor: 'rgba(62, 79, 224, 0.04)',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </FormControl>

                      {/* Columns Selection */}
                      <FormControl>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Columns
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => {
                              if (form.selectedColumns.length === AVAILABLE_COLUMNS.length) {
                                updateNewReportForm(form.id, 'selectedColumns', []);
                              } else {
                                updateNewReportForm(form.id, 'selectedColumns', AVAILABLE_COLUMNS.map((col) => col.id));
                              }
                            }}
                            sx={{
                              textTransform: 'none',
                              color: 'rgb(23, 166, 218)',
                            }}
                          >
                            {form.selectedColumns.length === AVAILABLE_COLUMNS.length ? 'Deselect All' : 'Select All'}
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {AVAILABLE_COLUMNS.map((column) => (
                            <Chip
                              key={column.id}
                              label={column.label}
                              onClick={() => toggleNewReportColumn(form.id, column.id)}
                              variant={form.selectedColumns.includes(column.id) ? 'filled' : 'outlined'}
                              sx={{
                                backgroundColor: form.selectedColumns.includes(column.id)
                                  ? 'rgba(62, 79, 224, 0.12)'
                                  : 'transparent',
                                borderColor: form.selectedColumns.includes(column.id)
                                  ? 'rgb(23, 166, 218)'
                                  : '#e0e0e0',
                                color: form.selectedColumns.includes(column.id) ? 'rgb(23, 166, 218)' : 'inherit',
                                '&:hover': {
                                  backgroundColor: form.selectedColumns.includes(column.id)
                                    ? 'rgba(62, 79, 224, 0.2)'
                                    : 'rgba(0, 0, 0, 0.04)',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </FormControl>

                      {/* Set Report Button */}
                      <Button
                        variant="contained"
                        onClick={() => handleSaveScheduledReport(form.id)}
                        disabled={!form.report_name.trim() || form.selectedColumns.length === 0 || form.isSaving}
                        sx={{
                          backgroundColor: 'rgb(23, 166, 218)',
                          textTransform: 'none',
                          alignSelf: 'flex-start',
                          '&:hover': {
                            backgroundColor: '#2d3db0',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#ccc',
                            color: '#999',
                          },
                        }}
                      >
                        {form.isSaving ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} sx={{ color: '#999' }} />
                            Saving...
                          </Box>
                        ) : (
                          'Set report'
                        )}
                      </Button>
                    </Box>
                  </Collapse>
                </Paper>
              ))}

              {/* Add New Report Link */}
              <Button
                variant="text"
                onClick={handleAddNewReportForm}
                startIcon={<AddIcon />}
                sx={{
                  color: 'rgb(23, 166, 218)',
                  textTransform: 'none',
                  alignSelf: 'flex-start',
                  '&:hover': {
                    backgroundColor: 'rgba(62, 79, 224, 0.04)',
                  },
                }}
              >
                Add new report
              </Button>
            </Box>
          </TabPanel>

          {/* Downloads Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Info Banner */}
              <Alert
                icon={<InfoIcon sx={{ color: 'rgb(23, 166, 218)' }} />}
                severity="info"
                sx={{
                  backgroundColor: '#e3f2fd',
                  '& .MuiAlert-icon': {
                    color: 'rgb(23, 166, 218)',
                  },
                }}
              >
                Find the downloaded reports from the past month (up to 30 reports).
              </Alert>

              {/* Loading State */}
              {isLoadingDownloads && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: 'rgb(23, 166, 218)' }} />
                </Box>
              )}

              {/* Empty State */}
              {!isLoadingDownloads && (!downloadedReports || downloadedReports.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No scheduled reports found
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Create a scheduled report to see downloads here.
                  </Typography>
                </Box>
              )}

              {/* Downloads Table */}
              {!isLoadingDownloads && downloadedReports && downloadedReports.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>File name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Creation date</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {downloadedReports.map((report) => (

                        <TableRow key={report.id}>
                          <TableCell>{report.report_name}</TableCell>
                          <TableCell>{new Date(report.generated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</TableCell>
                          <TableCell>
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<DownloadIcon />}
                              onClick={() => {
                                if (report.file_url) {
                                  window.open(report.file_url, '_blank');
                                }
                              }}
                              sx={{
                                color: 'rgb(23, 166, 218)',
                                textTransform: 'none',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                },
                              }}
                            >
                              Download
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </TabPanel>
        </Box>

        {/* Footer Actions */}
        {activeTab === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              p: 2,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: 'rgb(23, 166, 218)',
                color: 'rgb(23, 166, 218)',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'rgb(23, 166, 218)',
                  backgroundColor: 'rgba(62, 79, 224, 0.04)',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={!reportName.trim() || selectedColumns.length === 0 || isExporting}
              sx={{
                backgroundColor: reportName.trim() && selectedColumns.length > 0 && !isExporting ? 'rgb(23, 166, 218)' : '#ccc',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: reportName.trim() && selectedColumns.length > 0 && !isExporting ? '#2d3db0' : '#ccc',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#ccc',
                  color: '#999',
                },
              }}
            >
              {isExporting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: '#999' }} />
                  Exporting...
                </Box>
              ) : (
                'Export'
              )}
            </Button>
          </Box>
        )}

        {activeTab !== 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              p: 2,
              borderTop: '1px solid #e0e0e0',
            }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: 'rgb(23, 166, 218)',
                color: 'rgb(23, 166, 218)',
                textTransform: 'none',
                '&:hover': {
                  borderColor: 'rgb(23, 166, 218)',
                  backgroundColor: 'rgba(62, 79, 224, 0.04)',
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{
            width: '100%',
            backgroundColor: '#4caf50',
            color: '#fff',
            '& .MuiAlert-icon': {
              color: '#fff',
            },
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Drawer>
  );
};
